import React, { useEffect, useState } from "react";
import { Card, Col, Row, Table, Typography } from "antd";
import PDFIcon from "../../../../../assets/images/pdf.png";
import CsvIcon from "../../../../../assets/images/csv.png";
import { AnalyticsCardsSkeleton } from "../../../../../components/page-skeleton";
import http from "../../../../../services/http";
import moment from "moment";
import { convertSeconds, formatSearchParams } from "../../../../../utils";
import { ExitToApp } from "@mui/icons-material";

const { Text } = Typography;

function UserAnalytics({ selectedUser }) {
  const [isLoading, setLoading] = useState(false);
  const [data, setData] = useState({});

  const getUserAnalytics = async () => {
    setLoading(true);
    const { data } = await http.get(`user-analytics/${selectedUser}`);
    setData(data);
    setLoading(false);
  };
  const { hours, minutes, seconds } = convertSeconds(data?.averageTime ?? 0);

  const activities = data?.activities ?? [];
  const florenceRuns = activities.filter((a) => a?.action_type === "isp_run").length;
  const investorSearchRuns = activities.filter((a) => a?.action_type === "investors_search").length;
  const investorSearchActivities = activities.filter((a) => a?.action_type === "investors_search");
  const totalLeadsAdded = activities.filter((a) => a?.action_type === "lead_added").length;
  const totalLeadsListsCreated = activities.filter((a) => a?.action_type === "lead_list_created").length;

  useEffect(() => {
    if (selectedUser) getUserAnalytics();
  }, [selectedUser]);

  return (
    <>
      {isLoading ? (
        <div className="loader" style={{ paddingBottom: 100 }}>
          <AnalyticsCardsSkeleton count={8} />
        </div>
      ) : (
        <>
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12} lg={6}>
              <Card className="analytics-card">
                <h3>Logged in Time</h3>
                <h1 className="small">
                  {data?.loggedInTime
                    ? moment(data?.loggedInTime).format("Do MM YYYY, hh:mm a")
                    : "-"}
                </h1>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="analytics-card">
                <h3>Average time online?</h3>
                {data?.averageTime ? (
                  <h1>
                    {hours > 0 && `${hours}h`} {minutes > 0 && `${minutes}m`}{" "}
                    {seconds > 0 && `${seconds}s`}
                  </h1>
                ) : (
                  <h1>-</h1>
                )}
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="analytics-card">
                <h3>Total Companies</h3>
                <h1>{data?.userCompany ?? "-"}</h1>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="analytics-card">
                <h3>Organization</h3>
                <h1 className="small">{data?.organization ?? "-"}</h1>
              </Card>
            </Col>
          </Row>
          <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
            <Col xs={24} sm={12} lg={12}>
              <Card className="analytics-card">
                <h3>Total leads added</h3>
                <h1>{totalLeadsAdded}</h1>
                <p className="card-hint">Leads this user has added</p>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={12}>
              <Card className="analytics-card">
                <h3>Leads lists created</h3>
                <h1>{totalLeadsListsCreated}</h1>
                <p className="card-hint">Lead lists this user has created</p>
              </Card>
            </Col>
          </Row>
          <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
            <Col xs={24} sm={12}>
              <Card className="analytics-card">
                <h3>Florence runs</h3>
                <h1>{florenceRuns}</h1>
                <p className="card-hint">Number of times user ran Florence (AI list generation)</p>
              </Card>
            </Col>
            <Col xs={24} sm={12}>
              <Card className="analytics-card">
                <h3>Investor search runs</h3>
                <h1>{investorSearchRuns}</h1>
                <p className="card-hint">Number of times user ran investor search</p>
              </Card>
            </Col>
          </Row>
          <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
            <Col xs={24} md={8}>
              <Card className="analytics-card">
                <h3>Florence downloads</h3>
                <h1>{data?.buyersCsv + data?.buyersPDF + data?.buyerCRMExport}</h1>
                <div className="download-stats">
                  <div className="box">
                    <img src={PDFIcon} alt="" />
                    <div className="detail">
                      <h4>{data?.buyersPDF}</h4>
                      <p>Downloads</p>
                    </div>
                  </div>
                  <div className="box">
                    <img src={CsvIcon} alt="" />
                    <div className="detail">
                      <h4>{data?.buyersCsv}</h4>
                      <p>Downloads</p>
                    </div>
                  </div>
                  <div className="box">
                    <span className="thumb">
                      <ExitToApp />
                    </span>
                    <div className="detail">
                      <h4>{data?.buyerCRMExport}</h4>
                      <p>Downloads</p>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card className="analytics-card">
                <h3>Leads downloads</h3>
                <h1>{data?.leadsCsv + data?.leadsPDF + data?.leadsCRMExport}</h1>
                <div className="download-stats">
                  <div className="box">
                    <img src={PDFIcon} alt="" />
                    <div className="detail">
                      <h4>{data?.leadsPDF}</h4>
                      <p>Downloads</p>
                    </div>
                  </div>
                  <div className="box">
                    <img src={CsvIcon} alt="" />
                    <div className="detail">
                      <h4>{data?.leadsCsv}</h4>
                      <p>Downloads</p>
                    </div>
                  </div>
                  <div className="box">
                    <span className="thumb">
                      <ExitToApp />
                    </span>
                    <div className="detail">
                      <h4>{data?.leadsCRMExport}</h4>
                      <p>Downloads</p>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card className="analytics-card">
                <h3>Investor search downloads</h3>
                <h1>{data?.investorCsv + data?.investorPDF + data?.investorCRMExport}</h1>
                <div className="download-stats">
                  <div className="box">
                    <img src={PDFIcon} alt="" />
                    <div className="detail">
                      <h4>{data?.investorPDF}</h4>
                      <p>Downloads</p>
                    </div>
                  </div>
                  <div className="box">
                    <img src={CsvIcon} alt="" />
                    <div className="detail">
                      <h4>{data?.investorCsv}</h4>
                      <p>Downloads</p>
                    </div>
                  </div>
                  <div className="box">
                    <span className="thumb">
                      <ExitToApp />
                    </span>
                    <div className="detail">
                      <h4>{data?.investorCRMExport}</h4>
                      <p>Downloads</p>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
          <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
            <Col xs={24} sm={12}>
              <Card className="analytics-card activity-logs-card">
                <h3>Activity logs</h3>
                <p className="card-hint" style={{ marginBottom: 12 }}>
                  Recent user actions (display only)
                </p>
                <div className="activity-logs-list" style={{ maxHeight: 320, overflowY: "auto" }}>
                  {activities.length === 0 ? (
                    <div className="activity-logs-empty">
                      <Text type="secondary">No activity yet</Text>
                    </div>
                  ) : (
                    activities.map((item, index) => (
                      <Row key={index} gutter={12} className="activity-logs-item">
                        <Col xs={24} sm={12}>
                          <span className="activity-logs-date">
                            {item?.display_date ?? (item?.created_at ? moment(item.created_at).format("Do MMM YYYY, h:mm a") : "—")}
                          </span>
                        </Col>
                        <Col xs={24} sm={12}>
                          <span className="activity-logs-summary">
                            {item?.display_summary ?? item?.action_type ?? "—"}
                          </span>
                        </Col>
                      </Row>
                    ))
                  )}
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12}>
              <Card className="analytics-card investor-search-params-card">
                <h3>Recent investor searches (query params)</h3>
                <p className="card-hint" style={{ marginBottom: 16 }}>
                  Filters and params used by this user for investor search
                </p>
                <div className="investor-search-params-list" style={{ maxHeight: 320, overflowY: "auto" }}>
                  {investorSearchActivities.length === 0 ? (
                    <div className="investor-search-params-empty">
                      <Text type="secondary">No investor searches yet</Text>
                    </div>
                  ) : (
                    investorSearchActivities.map((item, index) => {
                      const params = item?.query_params ?? item?.params ?? item;
                      const rows = formatSearchParams(params);
                      const label = item?.created_at
                        ? moment(item.created_at).format("Do MMM YYYY, HH:mm a")
                        : `Search ${index + 1}`;
                      const hasRows = rows.length > 0;
                      return (
                        <div key={index} className="investor-search-params-item">
                          <div className="investor-search-params-item-label">{label}</div>
                          {hasRows ? (
                            <Table
                              size="small"
                              pagination={false}
                              dataSource={rows}
                              columns={[
                                { title: "Param", dataIndex: "paramName", key: "paramName", width: 180, render: (t) => <Text strong>{t}</Text> },
                                { title: "Value", dataIndex: "paramValue", key: "paramValue", ellipsis: true },
                              ]}
                              rowKey="_rowKey"
                            />
                          ) : params && typeof params === "object" && !Array.isArray(params) && Object.keys(params).length > 0 ? (
                            <pre style={{ margin: 0, fontSize: 12, background: "#f5f5f5", padding: 12, borderRadius: 6 }}>
                              {JSON.stringify(params, null, 2)}
                            </pre>
                          ) : (
                            <Text type="secondary">No params recorded for this search.</Text>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </>
  );
}

export default UserAnalytics;
